import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSettings } from '@/features/config/hooks/use-config';
import { Button } from '@/shared/ui/Button';

import { useEnablePush } from '../hooks/use-notifications';
import { isPushSupported } from '../lib/push';

/** Permission-request UX for push. Hidden entirely when push is unsupported,
 * unconfigured, or turned off via the runtime `notifications.push` app_setting. */
export function EnableNotifications(): JSX.Element | null {
  const { t } = useTranslation();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [outcome, setOutcome] = useState<'idle' | 'granted' | 'denied'>('idle');
  const enable = useEnablePush();
  const { flag } = useAppSettings();

  useEffect(() => {
    let active = true;
    void isPushSupported().then((s) => {
      if (active) setSupported(s);
    });
    return () => {
      active = false;
    };
  }, []);

  if (!flag('notifications.push')) return null;
  if (supported === null) return null;
  if (!supported) return <p className="muted">{t('notifications.unsupported')}</p>;

  return (
    <div className="stack">
      <p className="muted">{t('notifications.enablePrompt')}</p>
      <Button
        variant="outline"
        loading={enable.isPending}
        onClick={() =>
          enable.mutate(undefined, {
            onSuccess: (ok) => setOutcome(ok ? 'granted' : 'denied'),
          })
        }
      >
        {t('notifications.enableCta')}
      </Button>
      {outcome === 'granted' ? <p className="muted">{t('notifications.enabled')}</p> : null}
      {outcome === 'denied' ? (
        <p className="text-danger" role="alert">
          {t('notifications.denied')}
        </p>
      ) : null}
    </div>
  );
}
